import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, AlertCircle, Check, X } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useProfile } from '@/hooks/useProfile';
import { toast } from 'sonner';

// Blocklist of inappropriate words/patterns (basic list, expand as needed)
const BLOCKED_PATTERNS = [
  // Explicit/vulgar terms
  /\b(fuck|shit|ass|bitch|cunt|dick|cock|pussy|nigger|nigga|faggot|retard)\b/i,
  // Variations with numbers/symbols
  /\b(f+u+c+k+|sh+i+t+|a+s+s+|b+i+t+c+h+)\b/i,
  // Admin/mod impersonation
  /\b(admin|moderator|mod|support|official|staff)\b/i,
  // System terms
  /\b(system|root|null|undefined|anonymous)\b/i,
];

// Minimum/maximum length
const MIN_LENGTH = 3;
const MAX_LENGTH = 30;

// Allowed characters pattern
const VALID_CHARS_PATTERN = /^[a-zA-Z0-9_\-. ]+$/;

function validateDisplayName(name: string): { valid: boolean; error?: string } {
  const trimmed = name.trim();
  
  if (trimmed.length < MIN_LENGTH) {
    return { valid: false, error: `Name must be at least ${MIN_LENGTH} characters` };
  }
  
  if (trimmed.length > MAX_LENGTH) {
    return { valid: false, error: `Name must be less than ${MAX_LENGTH} characters` };
  }
  
  if (!VALID_CHARS_PATTERN.test(trimmed)) {
    return { valid: false, error: 'Only letters, numbers, spaces, underscores, hyphens, and periods allowed' };
  }
  
  // Check for blocked patterns
  for (const pattern of BLOCKED_PATTERNS) {
    if (pattern.test(trimmed)) {
      return { valid: false, error: 'This name is not allowed' };
    }
  }
  
  // Check for excessive repetition
  if (/(.)\1{4,}/.test(trimmed)) {
    return { valid: false, error: 'Name contains too many repeated characters' };
  }
  
  return { valid: true };
}

export function DisplayNameSetupDialog() {
  const { user } = useAuth();
  const { profile, loading: profileLoading, updateProfile, refetch } = useProfile();
  
  const [open, setOpen] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [isChecking, setIsChecking] = useState(false);
  const [isAvailable, setIsAvailable] = useState<boolean | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Show dialog when profile is loaded and display_name is null/empty
  useEffect(() => {
    if (!profileLoading && profile && !profile.display_name) {
      setOpen(true);
    }
  }, [profileLoading, profile]);

  // Check availability with debounce
  useEffect(() => {
    const trimmed = displayName.trim();
    
    // Validate locally first
    const validation = validateDisplayName(trimmed);
    if (!validation.valid) {
      setValidationError(validation.error || null);
      setIsAvailable(null);
      return;
    }
    
    setValidationError(null);
    setIsAvailable(null);
    setIsChecking(true);
    
    const timer = setTimeout(async () => {
      try {
        const { data, error } = await supabase
          .rpc('is_display_name_available', { name: trimmed });
        
        if (error) throw error;
        setIsAvailable(data);
      } catch (error) {
        console.error('Error checking availability:', error);
        setIsAvailable(null);
      } finally {
        setIsChecking(false);
      }
    }, 500);
    
    return () => clearTimeout(timer);
  }, [displayName]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const trimmed = displayName.trim();
    const validation = validateDisplayName(trimmed);
    
    if (!validation.valid) {
      setValidationError(validation.error || null);
      return;
    }
    
    if (!isAvailable) {
      return;
    }
    
    setIsSaving(true);
    
    const { error } = await updateProfile({ display_name: trimmed });
    
    if (error) {
      if (error.message.includes('unique') || error.message.includes('duplicate')) {
        setIsAvailable(false);
      } else {
        toast.error('Failed to set display name');
      }
    } else {
      toast.success('Welcome to Vaultsy!');
      setOpen(false);
      refetch();
    }
    
    setIsSaving(false);
  };

  // Don't render if not needed
  if (!user || profileLoading || !profile || profile.display_name) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={() => {/* Prevent closing without setting name */}}>
      <DialogContent className="sm:max-w-md" onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="text-2xl">Welcome to Vaultsy! 🎉</DialogTitle>
          <DialogDescription>
            Choose a unique display name that will be visible to other collectors. 
            This cannot be changed later, so choose wisely!
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="displayName">Display Name</Label>
            <div className="relative">
              <Input
                id="displayName"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Enter your display name"
                maxLength={MAX_LENGTH}
                className="pr-10"
                autoFocus
              />
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                {isChecking && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
                {!isChecking && isAvailable === true && <Check className="h-4 w-4 text-green-500" />}
                {!isChecking && isAvailable === false && <X className="h-4 w-4 text-destructive" />}
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              {displayName.trim().length}/{MAX_LENGTH} characters
            </p>
          </div>

          {validationError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{validationError}</AlertDescription>
            </Alert>
          )}

          {!validationError && isAvailable === false && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>This name is already taken</AlertDescription>
            </Alert>
          )}

          {!validationError && isAvailable === true && (
            <Alert className="border-green-500/50 bg-green-500/10">
              <Check className="h-4 w-4 text-green-500" />
              <AlertDescription className="text-green-700 dark:text-green-400">
                This name is available!
              </AlertDescription>
            </Alert>
          )}

          <Button 
            type="submit" 
            className="w-full" 
            disabled={isSaving || !isAvailable || !!validationError || displayName.trim().length < MIN_LENGTH}
          >
            {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Continue
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
